import { useStore } from "zustand";
import { useDashboardName } from "../../store/headerName";
import { useEffect, useRef, useState } from "react";
import FormItemInput from "../../components/form-item/form-item-input";
import FormItemInputSelect from "../../components/form-item/form-item-input-select";
import { Form } from "antd";
import FormCard from "../../components/card/FormCard";
import ImageCard from "../../components/card/ImageCard";
import FormItemInputTextarea from "../../components/form-item/form-item-input-textarea";
import FormButton from "../../components/FormButton";
import FormItemInputNumber from "../../components/form-item/form-item-input-number";
import "quill/dist/quill.snow.css";
import { $api } from "../../helper";
import { useNotify } from "../../helper/notify";
import { GetFullMediaPath, requireRules } from "../../helper/method";
import PreviewFile from "../../components/PreviewFile";
import { statusShowMock, tokenMock } from "../../helper/mock";
import { useNavigate, useSearchParams } from "react-router";
import Quill from "quill";

export default function AddProduct() {
  const editorRef = useRef<HTMLDivElement | null>(null);
  const quillRef = useRef<Quill | null>(null);
  const notify = useNotify();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const u_id = searchParams.get("u_id");
  const [file, setFile] = useState<
    {
      file: File | undefined;
      fileUrl: string;
      id: string;
    }[]
  >([]);
  const [form] = Form.useForm();
  const { data } = $api.useQuery("get", "/site-api/v1/product-form/");
  const detail = $api.useMutation("get", "/site-api/v1/product-detail/");
  const { mutate, isPending } = $api.useMutation(
    "post",
    "/site-api/v1/product/",
  );
  const update = $api.useMutation("put", "/site-api/v1/product/");
  const { setDashboardName } = useStore(useDashboardName);
  useEffect(() => {
    setDashboardName(u_id ? "ویرایش محصول" : "ایجاد محصول جدید");
    if (u_id) {
      detail.mutate({
        params: {
          query: {
            product_u_id: u_id,
          },
        },
        headers: {
          Authorization: tokenMock,
          lang: "fa",
        },
      });
    }
  }, []);

  useEffect(() => {
    if (!editorRef.current) return;

    if (quillRef.current) return;

    quillRef.current = new Quill(editorRef.current, {
      theme: "snow",
      modules: {
        toolbar: [
          [{ header: [1, 2, 3, 4, 5, 6, false] }],
          ["bold", "italic", "underline", "strike"],
          [{ color: [] }, { background: [] }],
          [{ direction: "rtl" }],
          [{ align: [] }],
          ["link"],
          ["clean"],
        ],
      },
    });
  }, []);

  useEffect(() => {
    if (detail.data) {
      setFile(
        detail.data.data.image?.map((e) => ({
          fileUrl: GetFullMediaPath(e.image),
          file: undefined,
          id: e.u_id,
        })),
      );
      quillRef.current?.clipboard.dangerouslyPasteHTML(
        detail.data.data.full_description,
      );
      Object.entries(detail.data?.data).forEach(([key, value]) => {
        form.setFieldValue(key, value?.toString());
      });
    }
  }, [detail.data]);

  useEffect(() => {
    if (quillRef.current) {
      quillRef.current.format("align", "right");
      quillRef.current.on("text-change", () => {
        const value =
          Number(quillRef.current?.editor.delta.ops[0].insert?.length) <= 1
            ? ""
            : quillRef.current && quillRef.current.root.innerHTML;

        form.setFieldValue("full_description", value);
        form.validateFields(["full_description"]);
      });
    }
  }, [quillRef?.current?.editor.getText]);

  const onFinish = (
    values: {
      product_u_id: string;
      title: string;
      status: string;
      category_u_id: string;
      price: string;
      short_description: string;
      full_description: string;
      seo_title: string;
      seo_description: string;
      slug: string;
      price_with_discount?: string;
    },
    redirect = true,
  ) => {
    const formData = new FormData();
    formData.append("product_u_id", values.product_u_id);
    formData.append("slug", values.slug);
    formData.append("title", values.title);
    formData.append("status", values.status);
    formData.append("category_u_id", values.category_u_id);
    formData.append("price", values.price);
    formData.append("short_description", values.short_description);
    formData.append("full_description", values.full_description);
    formData.append("seo_title", values.seo_title);
    formData.append("seo_description", values.seo_description);
    if (values.price_with_discount) {
      formData.append("price_with_discount", values.price_with_discount);
    }
    if (u_id) formData.append("check_product_u_id", u_id);
    if (!u_id) {
      file?.forEach((e) => {
        formData.append("images", e.file ?? e.fileUrl);
      });
    } else {
      file?.forEach((item) => {
        if (!item.file) {
          formData.append("old_images", item.id);
        }
        if (item.file && item.fileUrl) {
          formData.append("images", item.file);
        }
      });
    }

    if (u_id) {
      update.mutate(
        {
          body: formData,
          headers: {
            Authorization: tokenMock,
            lang: "fa",
          },
        },
        {
          onSuccess: (data) => {
            notify.success(data.message);
            if (redirect) {
              navigate("/product-list");
            } else {
              setTimeout(() => {
                window.location.href = window.location.pathname;
              }, 1000);
            }
          },
        },
      );
    } else {
      mutate(
        {
          body: formData,
          headers: {
            Authorization: tokenMock,
            lang: "fa",
          },
        },
        {
          onSuccess: (data) => {
            notify.success(data.message);
            if (redirect) {
              navigate("/product-list");
            } else {
              setTimeout(() => {
                window.location.href = window.location.pathname;
              }, 1000);
            }
          },
        },
      );
    }
  };

  return (
    <Form
      form={form}
      onFinish={onFinish}
      layout="vertical"
      className="flex! flex-col! gap-6!"
    >
      <FormCard
        subTitle="تعریف و تنظیمات پایه مربوط به محصول"
        title="اطلاعات اصلی محصول"
      >
        <div className="grid sm:grid-cols-2 grid-cols-1 w-full gap-6">
          <FormItemInput
            name={"title"}
            rules={requireRules("عنوان محصول")}
            className="col-span-1"
            label="عنوان محصول"
            inputProps={{
              placeholder: "لطفا عنوان محصول خود را وارد کنید.",
            }}
          />
          <FormItemInput
            name={"slug"}
            rules={requireRules("اسلاگ")}
            className="col-span-1"
            label="اسلاگ"
            inputProps={{
              placeholder: "لطفا اسلاگ خود را وارد کنید.",
            }}
          />
          <FormItemInputSelect
            rules={requireRules("وضعیت")}
            name={"status"}
            className="col-span-1"
            label="وضعیت"
            inputProps={{
              placeholder: "لطفا وضعیت نمایش محصول خود را انتخاب کنید.",
              options: statusShowMock,
            }}
          />
          <FormItemInputSelect
            className="col-span-1"
            rules={requireRules("محصول")}
            name={"product_u_id"}
            label="لیست محصولات"
            inputProps={{
              showSearch: {
                optionFilterProp: "label",
              },
              placeholder: "لطفا دسته بندی محصول خود را انتخاب کنید.",
              options: data?.data.products?.map((e) => ({
                label: e.name,
                value: e.u_id,
              })),
            }}
          />
          <FormItemInputSelect
            className="col-span-1"
            rules={requireRules("دسته بندی")}
            name={"category_u_id"}
            label="دسته بندی"
            inputProps={{
              placeholder: "لطفا دسته بندی محصول خود را انتخاب کنید.",
              options: data?.data.categories?.map((e) => ({
                label: e.title,
                value: e.u_id,
              })),
            }}
          />
          <FormItemInputNumber
            className="col-span-1 w-full"
            rules={requireRules("قیمت")}
            name={"price"}
            label="قیمت"
            inputProps={{
              placeholder: "قیمت محصول را وارد کنید",
            }}
          />
          <FormItemInputNumber
            className="col-span-1 w-full"
            name={"price_with_discount"}
            label="قیمت محصول با تخفیف"
            inputProps={{
              placeholder: "قیمت محصول با تخفیف را وارد کنید",
            }}
          />
        </div>
      </FormCard>
      <FormCard subTitle="توضیح کوتاه و کامل محصول" title="توضیحات محصول">
        <div className="grid sm:grid-cols-2 grid-cols-1 w-full gap-6">
          <FormItemInputTextarea
            rules={requireRules("توضیحات کوتاه")}
            name={"short_description"}
            className="col-span-2"
            label="توضیحات کوتاه"
            inputProps={{
              placeholder: "توضیحات مختصر...",
              rows: 8,
            }}
          />
          <Form.Item
            rules={requireRules("توضیحات کامل")}
            className="col-span-2"
            name="full_description"
            label="توضیحات کامل"
          >
            <div
              dir="auto"
              className="w-full border-r border-l border-b border-grayscale-200 h-96! rounded-br-xl rounded-bl-xl"
              lang="fa"
              ref={editorRef}
            />
          </Form.Item>
        </div>
      </FormCard>
      <FormCard subTitle="تصاویر مربوط به نمایش محصول" title="تصاویر محصول">
        <div className="flex w-full gap-6 flex-col">
          <div className="w-full">
            <ImageCard
              setFile={setFile}
              multiple
              file={file}
              id="image1"
              name="images"
            />
          </div>
          <div className="grid-cols-8 grid gap-6">
            {file?.map((e, i) => (
              <div className="col-span-1 aspect-square">
                <PreviewFile
                  id={e.id}
                  url={e.fileUrl}
                  setFile={setFile}
                  key={i + e.id}
                />
              </div>
            ))}
          </div>
        </div>
      </FormCard>
      <FormCard
        title="اطلاعات سئو"
        subTitle="بهینه‌سازی محصول برای موتورهای جستجو"
      >
        <div className="grid sm:grid-cols-2 grid-cols-1 w-full gap-6">
          <FormItemInput
            rules={requireRules("عنوان سئو")}
            name={"seo_title"}
            label="عنوان سئو"
            className="col-span-1"
            inputProps={{
              placeholder: "لطفا عنوان سئو خود را وارد کنید.",
            }}
          />
          <FormItemInputTextarea
            rules={requireRules("توضیحات سئو")}
            name={"seo_description"}
            label="توضیحات"
            className="col-span-1"
            inputProps={{
              className: "resize-none!",
              rows: 4,
              placeholder: "لطفا توضیحات اضافی سئو خود را بنویسید...",
            }}
          />
        </div>
      </FormCard>
      <FormButton isPending={isPending || update.isPending} />
    </Form>
  );
}
